import type { Localized } from '../i18n'
import type {
  EdoDecision, EdoStage, EdoStatus, EdoType,
} from './types'

/**
 * Человекочитаемые подписи enum-кодов анкеты подбора (ТЗ-2026-011). Единый словарь для формы
 * создания (processes.tsx) и карточки заявки (CardDetails) — коды в payload не пересекаются
 * между полями, поэтому одна плоская карта. AZ берётся фолбэком по AZ_DB[ru] в tr().
 */
export const REC_ENUM_LABEL: Record<string, Localized> = {
  // employmentRate
  full: { ru: 'Полная ставка', en: 'Full-time' },
  part: { ru: 'Неполная ставка', en: 'Part-time' },
  // workSchedule
  '5d': { ru: '5-дневный', en: '5-day week' },
  '6d': { ru: '6-дневный', en: '6-day week' },
  '2shift': { ru: '2-сменный', en: '2 shifts' },
  '3shift': { ru: '3-сменный', en: '3 shifts' },
  // vacancyType
  new: { ru: 'Новая штатная единица', en: 'New position' },
  budgeted: { ru: 'Бюджетированная', en: 'Budgeted' },
  // staffUnitType
  intern: { ru: 'Стажёр', en: 'Intern' },
  temporary: { ru: 'Временная', en: 'Temporary' },
  project: { ru: 'Проектная', en: 'Project' },
}

/** Опция справочника анкеты подбора: value хранится в payload, label локализуется. */
export interface RecOption { value: string; label: Localized }

/**
 * Справочники формы подбора (причины/опыт/образование/виды проверки).
 * ⚠ value — КАНОНИЧЕСКОЕ хранимое значение; исторически это русский текст, и он уже лежит
 * в payload существующих заявок (дашборд и отчётность бэка читают payload.reasons как есть).
 * Менять value на стабильные коды можно ТОЛЬКО синхронно с бэкендом; до этой договорённости
 * локализуем label, value не трогаем. Старые значения карточка рендерит через recLabel():
 * неизвестное value показывается как есть.
 */
export const REC_REASONS: RecOption[] = [
  { value: 'Увеличение объёма работ', label: { ru: 'Увеличение объёма работ', en: 'Increased workload' } },
  { value: 'Отсутствие сотрудника по должности', label: { ru: 'Отсутствие сотрудника по должности', en: 'Position is vacant' } },
  { value: 'Создание новой структуры', label: { ru: 'Создание новой структуры', en: 'New unit created' } },
  { value: 'Запуск нового проекта', label: { ru: 'Запуск нового проекта', en: 'New project launch' } },
  { value: 'Увольнение сотрудника', label: { ru: 'Увольнение сотрудника', en: 'Employee resignation' } },
  { value: 'Изменение подразделения/должности', label: { ru: 'Изменение подразделения/должности', en: 'Department/position change' } },
  { value: 'Длительная болезнь (>30 дней)', label: { ru: 'Длительная болезнь (>30 дней)', en: 'Long-term illness (>30 days)' } },
  { value: 'Выход в декрет', label: { ru: 'Выход в декрет', en: 'Maternity leave' } },
]
/** Причины, требующие поля «Кого замещает» (сравнение по value, не по подписи). */
export const REC_REPLACE_REASONS = ['Увольнение сотрудника', 'Изменение подразделения/должности']
export const REC_EXPERIENCE: RecOption[] = [
  { value: 'Без опыта', label: { ru: 'Без опыта', en: 'No experience' } },
  { value: 'От 1 года', label: { ru: 'От 1 года', en: '1+ year' } },
  { value: '1–3 года', label: { ru: '1–3 года', en: '1–3 years' } },
  { value: '3–5 лет', label: { ru: '3–5 лет', en: '3–5 years' } },
  { value: '5–10 лет', label: { ru: '5–10 лет', en: '5–10 years' } },
  { value: '10+ лет', label: { ru: '10+ лет', en: '10+ years' } },
]
export const REC_EDUCATION: RecOption[] = [
  { value: 'Среднее', label: { ru: 'Среднее', en: 'Secondary' } },
  { value: 'Ср. специальное', label: { ru: 'Ср. специальное', en: 'Vocational' } },
  { value: 'Высшее', label: { ru: 'Высшее', en: 'Higher education' } },
  { value: 'Магистратура', label: { ru: 'Магистратура', en: "Master's degree" } },
  { value: 'MBA', label: { ru: 'MBA', en: 'MBA' } },
  { value: 'PhD', label: { ru: 'PhD', en: 'PhD' } },
  { value: 'Не важно', label: { ru: 'Не важно', en: 'Any' } },
]
export const REC_TEST_TYPES: RecOption[] = [
  { value: 'IQ / логика', label: { ru: 'IQ / логика', en: 'IQ / logic' } },
  { value: 'Математика', label: { ru: 'Математика', en: 'Math' } },
  { value: 'Знание ПО', label: { ru: 'Знание ПО', en: 'Software skills' } },
  { value: 'Навыки вождения', label: { ru: 'Навыки вождения', en: 'Driving skills' } },
]

/** Подпись значения справочника; неизвестное (старое/чужое) value показываем как есть. */
export const recLabel = (opts: RecOption[], value: string): Localized | string =>
  opts.find((o) => o.value === value)?.label ?? value

/**
 * Подписи перечислений формы договора (ТЗ-2026-015). Коды — зеркало серверного
 * create-contract.dto.ts; хранится код, локализуется подпись (в отличие от подбора, где value
 * исторически русский текст). AZ подтягивается фолбэком по AZ_DB[ru] в tr().
 */
export const CONTRACT_ENUM_LABEL: Record<string, Localized> = {
  // company — от неё зависит маршрут (§4.2, §5.2)
  AS_QRUP: { ru: 'AS Qrup İnvestment', en: 'AS Qrup Investment' },
  AZMAN: { ru: 'AZMAN MTK', en: 'AZMAN MTK' },
  AS_INSHAAT: { ru: 'AS İnşaat', en: 'AS İnşaat' },
  AS_DEVELOPMENT: { ru: 'AS Development', en: 'AS Development' },
  OTHER: { ru: 'Другая компания группы', en: 'Other group company' },
  // source — откуда взят текст договора (правки прототипа 24.08.2026)
  internal_template: { ru: 'Внутренний шаблон договора', en: 'Internal template' },
  counterparty_template: { ru: 'Шаблон контрагента', en: 'Counterparty template' },
  // docType
  contract: { ru: 'Договор', en: 'Contract' },
  addendum: { ru: 'Дополнительное соглашение', en: 'Addendum' },
  // az явно, а не через AZ_DB: короткие слова, общий словарь перевёл бы их и вне договора
  annex: { ru: 'Дополнение к договору', en: 'Annex', az: 'Əlavə' },
  other: { ru: 'Другое', en: 'Other' },
  // serviceKind — с 27.08.2026 это и есть «Тип контракта»: прежнее одноимённое поле
  // (разовый/рамочный/абонентский) схлопнуто сюда. «Аренда» убрана 27.08 и возвращена 13.10.2026
  // по запросу бизнеса (AZ — «İcarə» из общего словаря, как у товаров и работ).
  // `services` держим ЗДЕСЬ явно: раньше этот код покрывался записью из docType («Договор
  // оказания услуг»), а её убрали вместе со старым списком типов — и чип остался без подписи.
  services: { ru: 'Услуги', en: 'Services', az: 'Xidmət' },
  goods: { ru: 'Товары', en: 'Goods' },
  works: { ru: 'Работы', en: 'Works' },
  rent: { ru: 'Аренда', en: 'Lease' },
  // hasAdditionalAppendix — boolean хранится в payload, строки нужны форме и аудиту правок
  true: { ru: 'С дополнительным приложением', en: 'With an additional appendix', az: 'Əlavə ilə' },
  false: { ru: 'Без дополнительного приложения', en: 'Without an additional appendix', az: 'Əlavəsiz' },
  // duration — справочный срок; точные даты проставляются в финале
  '1y': { ru: '1 год', en: '1 year' },
  '2y': { ru: '2 года', en: '2 years' },
  '3y': { ru: '3 года', en: '3 years' },
  '5y': { ru: '5 лет', en: '5 years' },
  perpetual: { ru: 'Бессрочный', en: 'Perpetual' },
  // counterpartyKind — местный резидент Азербайджана или зарубежный (требование 12.08.2026)
  local: { ru: 'Местный', en: 'Local' },
  foreign: { ru: 'Зарубежный', en: 'Foreign' },
  // counterpartyType — вид зарубежного контрагента (19.10.2026)
  legalEntity: { ru: 'Юридическое лицо', en: 'Legal entity', az: 'Hüquqi şəxs' },
  naturalPerson: { ru: 'Физическое лицо', en: 'Individual', az: 'Fiziki şəxs' },
  // paymentType
  prepaid: { ru: 'Аванс', en: 'Prepaid' },
  on_fact: { ru: 'По факту', en: 'On completion' },
  staged: { ru: 'Поэтапно', en: 'By stages' },
  prepaid_staged: { ru: 'Аванс + поэтапно', en: 'Prepaid + by stages' },
  // currency — без записей здесь чипы валют рендерились пустыми (t(undefined) → '')
  AZN: { ru: 'AZN — манат', en: 'AZN — manat' },
  USD: { ru: 'USD — доллар США', en: 'USD — US dollar' },
  EUR: { ru: 'EUR — евро', en: 'EUR — euro' },
  RUB: { ru: 'RUB — российский рубль', en: 'RUB — Russian rouble' },
  GBP: { ru: 'GBP — фунт стерлингов', en: 'GBP — pound sterling' },
}

/**
 * Подписи полей payload в диффе версии документа (AuditTimeline, событие `revised`).
 * Ключи — объединение editablePayloadKeys бэкенда (process-registry: FREEFORM + CONTRACT + IT_WORKS);
 * неизвестный ключ рендерится как есть. Enum-значения локализуются через CONTRACT_ENUM_LABEL.
 */
export const REVISED_FIELD_LABEL: Record<string, Localized> = {
  title: { ru: 'Тема', en: 'Title' },
  body: { ru: 'Текст', en: 'Text' },
  subject: { ru: 'Предмет', en: 'Subject' },
  source: { ru: 'Источник документа', en: 'Document source' },
  docType: { ru: 'Тип документа', en: 'Document type' },
  otherType: { ru: 'Уточнение типа', en: 'Type details' },
  docNo: { ru: 'Номер договора', en: 'Contract number' },
  contractNo: { ru: 'Номер основного договора', en: 'Master contract no.' },
  serviceKind: { ru: 'Тип контракта', en: 'Contract type' },
  duration: { ru: 'Длительность', en: 'Duration' },
  counterpartyName: { ru: 'Контрагент', en: 'Counterparty' },
  amount: { ru: 'Сумма', en: 'Amount' },
  currency: { ru: 'Валюта', en: 'Currency' },
  paymentType: { ru: 'Вид оплаты', en: 'Payment' },
  // Старые события аудита сохраняют прежний ключ — подпись должна остаться человекочитаемой.
  vatRate: { ru: 'Условия НДС', en: 'VAT terms', az: 'ƏDV şərtləri' },
  vatTerms: { ru: 'Условия НДС', en: 'VAT terms', az: 'ƏDV şərtləri' },
  hasAdditionalAppendix: { ru: 'Приложение к договору', en: 'Contract appendix', az: 'Müqaviləyə əlavə' },
  comment: { ru: 'Комментарий', en: 'Comment' },
  // IT-работы (ТЗ-2026-АКТ001): ключи editablePayloadKeys(IT_WORKS); amount/currency/comment — общие выше.
  period: { ru: 'Отчётный период', en: 'Reporting period', az: 'Hesabat dövrü' },
  periodFrom: { ru: 'Период с', en: 'Period from', az: 'Dövr başlanğıcı' },
  periodTo: { ru: 'Период по', en: 'Period to', az: 'Dövr sonu' },
  contractorId: { ru: 'Подрядчик', en: 'Contractor', az: 'Podratçı' },
  finalApproverId: { ru: 'Финальный утверждающий', en: 'Final approver', az: 'Yekun təsdiq edən' },
  recipients: { ru: 'Получатели', en: 'Recipients', az: 'Alıcılar' },
  reports: { ru: 'Отчёты', en: 'Reports', az: 'Hesabatlar' },
  modules: { ru: 'Модули', en: 'Modules', az: 'Modullar' },
  invoiceUploadId: { ru: 'Счёт подрядчика', en: 'Contractor invoice', az: 'Podratçının hesab-fakturası' },
}

export const EDO_TYPE_LABEL: Record<EdoType, string> = {
  VACATION: 'Отпуск',
  HIRE: 'Приём',
  TERMINATION: 'Увольнение',
  TIME_OFF: 'Отгул',
  STUDY_LEAVE: 'Учебный отпуск',
  BUSINESS_TRIP: 'Командировка',
  RECRUITMENT: 'Подбор персонала',
  SICK_LEAVE: 'Больничный',
  PATERNITY_LEAVE: 'Отцовский отпуск',
  MATERNITY_LEAVE: 'Материнский отпуск',
  CHILD_CARE_LEAVE: 'Отпуск по уходу за ребёнком',
  FREEFORM: 'Документ произвольной формы',
  CONTRACT: 'Согласование договора',
  IT_WORKS: 'Принятие IT-работ',
}

export const STATUS_LABEL: Record<EdoStatus, string> = {
  AWAITING_TERMINATION_POSTING: 'Черновик увольнения ожидает проведения в 1С',
  AWAITING_HIRE_POSTING: 'Черновик создан, ожидает проведения в 1С',
  DRAFT: 'Черновик',
  MGR_REVIEW: 'У руководителя',
  SUP_REVIEW: 'У руководителя СП',
  HR_REVIEW: 'У HR',
  SIGNING: 'На подписи',
  APPROVED: 'Согласовано',
  POSTED: 'Проведено',
  POST_FAILED: 'Ошибка проводки',
  REWORK: 'На доработке',
  REJECTED: 'Отклонено',
  NOT_REVIEWED: 'Не рассмотрено в срок',
  AWAITING_ACCRUAL: 'Начисление (бухгалтер)',
  TRAVEL_BOOKING: 'Оформление референтом',
  EXPENSE_REVIEW: 'Расчёт бухгалтера',
  ORDER_CREATION: 'Приказ: подпись и скан',
  CFO_APPROVAL: 'Согласование начисления (CFO)',
  CEO_REVIEW: 'У Ген. директора',
  IN_PROGRESS: 'В работе',
  SEARCHING: 'Поиск ведётся',
  CLOSED: 'Закрыта (кандидат найден)',
  CANCELLED: 'Отменена',
  REGISTERED: 'Зарегистрирован',
  DSMF_NOTIFYING: 'Уведомление в DSMF',
  IN_APPROVAL: 'На согласовании',
  AWAITING_SIGNED_COPY: 'Ожидает подписанный экземпляр',
  POSTING: 'Передаётся в 1С',
  EXIT_INTERVIEW: 'Exit-интервью',
}

/** Which actions are available at each EDO stage (mirrors backend ALLOWED_DECISIONS). */
export const STAGE_ACTIONS: Record<EdoStage, EdoDecision[]> = {
  MANAGER: ['approve', 'reject', 'rework'],
  SUPERVISOR: ['approve', 'reject'],
  HR: ['approve', 'reject', 'rework'],
  SIGNATURE: ['sign', 'reject'],
  ACCOUNTANT: ['approve', 'reject'],
  // Командировка v1.2 §2.2: точки отклонения — Руководитель, Рук.СП, CEO. Хвост после
  // подтверждения CEO (бронь → расчёт → CFO → начисление) — только approve. ИСКЛЮЧЕНИЕ — ORDER:
  // ОТВ-ИТ-2026-003 (Q3) добавил шагу приказа «Отклонить» (ГД/CEO не подписал приказ; причина обяз.,
  // скан не нужен). Зеркало бэкендового ALLOWED_DECISIONS[ORDER].
  TRAVEL: ['approve'],
  EXPENSE_REVIEW: ['approve'],
  ORDER: ['approve', 'reject'],
  CFO: ['approve'],
  ACCRUAL: ['approve'],
  REWORK: ['resubmit', 'cancel'],
  // Подбор: ГД — approve/reject; старт поиска — approve (generic decide). Закрытие (CLOSE) идёт
  // НЕ через generic decide, а через отдельный эндпоинт с файлом-резюме → в UI своя кнопка.
  CEO: ['approve', 'reject'],
  SEARCH: ['approve'],
  CLOSE: ['approve'],
  // DSMF — подтверждение факта ручной отправки. Отклонить информирование госоргана нельзя.
  DSMF: ['approve'],
  // Произвольный документ: шаг маршрута инициатора — обычное согласование, подписи в процессе нет.
  CUSTOM: ['approve', 'reject', 'rework'],
  // Договор (23.09.2026): автор отправляет в 1С со сканом и сроком либо закрывает «не подписан».
  SIGNED_COPY: ['approve', 'cancel'],
  // Увольнение: результат интервью уходит своей формой (ExitInterviewForm), не общими кнопками.
  EXIT_INTERVIEW: ['approve'],
  // IT-работы (ТЗ-2026-АКТ001): на этапе модулей общих решений нет (R37) — согласующий решает строки
  // своей панелью (POST /edo/it-works/:id/lines). Финал — обычное утверждение, отказ и доработка.
  IT_MODULES: [],
  IT_FINAL: ['approve', 'reject', 'rework'],
}

/**
 * Увольнение (ТЗ-2026-008, 07.10.2026) — зеркало бэкендового TERMINATION_DECISIONS: у согласующих
 * нет «Отклонить», только «Вернуть на доработку»; шаг приказа после решения CEO — только «Выполнено».
 */
const TERMINATION_ACTIONS: Partial<Record<EdoStage, EdoDecision[]>> = {
  MANAGER: ['approve', 'rework'],
  SUPERVISOR: ['approve', 'rework'],
  HR: ['approve', 'rework'],
  CEO: ['approve', 'rework'],
  ORDER: ['approve'],
}

/** Решения шага с учётом типа процесса. */
export function stageActions(type: EdoType | undefined, stage: EdoStage): EdoDecision[] {
  return (type === 'TERMINATION' ? TERMINATION_ACTIONS[stage] : undefined) ?? STAGE_ACTIONS[stage]
}
